import React from "react";
import { graphql, useFragment } from "react-relay";
import { matchPath, useLocation } from "react-router";

import EventEmitter from "events";
import { v4 } from "uuid";

import { useLazyLoadQuery, useMutationPromise } from "~/lib/relayUtil";
import { getFirstUserFacingError, inNewCompanionMode } from "~/lib/utils";
import { useAnalyticsTrack } from "~/meetingRecorder/utils";
import { useNavDispatchPanel } from "~/nav";
import { useCompanionModeNavDispatchPanel } from "~/nav/CompanionModeNavbar";
import { useLibraryQueryParams } from "~/recordings/Filters/Filters";
import type { RouteParams as LibraryRouteParams } from "~/recordings/LibraryChannelPage";
import type { URLParams as StreamRouteParams } from "~/stream/StreamHeader/StreamHeader";
import { toast } from "~/ui";

import type { AskFellowHooks_useAskCopilotSendMessage_conversation$key } from "~/__generated__/AskFellowHooks_useAskCopilotSendMessage_conversation.graphql";
import type { AskFellowHooks_useAskFellowButton_note$key } from "~/__generated__/AskFellowHooks_useAskFellowButton_note.graphql";
import type { AskFellowHooksClearConversationContextMutation } from "~/__generated__/AskFellowHooksClearConversationContextMutation.graphql";
import type { AskFellowHooksContextFromURLQuery } from "~/__generated__/AskFellowHooksContextFromURLQuery.graphql";
import type { AskFellowHooksCreateConversationMutation } from "~/__generated__/AskFellowHooksCreateConversationMutation.graphql";
import type { AskFellowHooksDeleteConversationMutation } from "~/__generated__/AskFellowHooksDeleteConversationMutation.graphql";
import type { AskFellowHooksRenameConversationMutation } from "~/__generated__/AskFellowHooksRenameConversationMutation.graphql";
import type { AskFellowHooksSendMessageViaConversationMutation } from "~/__generated__/AskFellowHooksSendMessageViaConversationMutation.graphql";
import type { AskFellowHooksSetConversationContextMutation } from "~/__generated__/AskFellowHooksSetConversationContextMutation.graphql";

export function useCreateConversationMutation() {
	const [commit, isInFlight] = useMutationPromise<AskFellowHooksCreateConversationMutation>(graphql`
		mutation AskFellowHooksCreateConversationMutation($contextId: ID, $filters: JSONString) {
			createAskCopilotConversationForUser(contextId: $contextId, filters: $filters) {
				conversation {
					id
					context {
						name
						filters
					}
					suggestedQuestions {
						id
						...AskFellowSuggestedQuestions_SuggestedQuestionButton_suggestedQuestion
					}
				}
			}
		}
	`);

	const createConversation = React.useCallback(
		async (contextId?: string | null, filters?: Record<string, unknown>) => {
			await commit({ variables: { contextId, filters: filters ? JSON.stringify(filters) : null } });
		},
		[commit],
	);

	return [createConversation, isInFlight] as const;
}

export function useClearConversationContextMutation() {
	const [commit, isInFlight] = useMutationPromise<AskFellowHooksClearConversationContextMutation>(graphql`
		mutation AskFellowHooksClearConversationContextMutation($conversationId: ID!) {
			clearContextForAskCopilotConversation(conversationId: $conversationId) {
				conversation {
					id
					context {
						name
						filters
					}
					suggestedQuestions {
						id
						...AskFellowSuggestedQuestions_SuggestedQuestionButton_suggestedQuestion
					}
				}
			}
		}
	`);

	const clearConversationContext = React.useCallback(
		async (conversationId: string) => {
			await commit({ variables: { conversationId } });
		},
		[commit],
	);

	return [clearConversationContext, isInFlight] as const;
}

export function useSetConversationContextMutation() {
	const [commit, isInFlight] = useMutationPromise<AskFellowHooksSetConversationContextMutation>(graphql`
		mutation AskFellowHooksSetConversationContextMutation($conversationId: ID!, $contextId: ID, $filters: JSONString) {
			setContextForAskCopilotConversation(conversationId: $conversationId, contextId: $contextId, filters: $filters) {
				conversation {
					...AskFellowContext_conversation
					suggestedQuestions {
						id
						...AskFellowSuggestedQuestions_SuggestedQuestionButton_suggestedQuestion
					}
				}
			}
		}
	`);

	const setConversationContext = React.useCallback(
		async (conversationId: string, contextId?: string | null, filters?: Record<string, unknown>) => {
			await commit({
				variables: { conversationId, contextId, filters: filters ? JSON.stringify(filters) : undefined },
			});
		},
		[commit],
	);

	return [setConversationContext, isInFlight] as const;
}

export function useAskFellowContextFromURL() {
	const location = useLocation();
	const libraryAllMeetingsRouteParams = matchPath<LibraryRouteParams>(location.pathname, { path: "/library/" });
	const libraryMyMeetingsRouteParams = matchPath<LibraryRouteParams>(location.pathname, {
		path: "/library/my-meetings/",
	});
	const libraryChannelRouteParams = matchPath<LibraryRouteParams>(location.pathname, {
		path: "/library/c/:channelId/",
	});
	const streamRouteParams = matchPath<StreamRouteParams>(location.pathname, { path: "/stream/:id/" });
	const meetingsRouteParams = matchPath<StreamRouteParams>(location.pathname, { path: "/meetings/:event/" });
	const { participation, attendeeEmails, attendeeType, date, postTypes, trackers, companyDomain } =
		useLibraryQueryParams();

	const baseContextFilters = Object.fromEntries(
		Object.entries({
			/* eslint-disable camelcase */
			attendee_emails: attendeeEmails?.value,
			date: date?.value,
			company_domain: companyDomain?.value,
			/* eslint-enable camelcase */
		}).filter(([_, value]) => value !== undefined),
	);

	const contextFilters = Object.fromEntries(
		Object.entries({
			...baseContextFilters,
			/* eslint-disable camelcase */
			meeting_type: participation?.value,
			attendee_type: attendeeType?.value,
			post_types: postTypes?.value && Array.from(postTypes.value),
			tracker_ids: trackers?.value,
			/* eslint-enable camelcase */
		}).filter(([_, value]) => value !== undefined),
	);

	let id: string | undefined = undefined;
	let guid: string | undefined = undefined;

	if (libraryChannelRouteParams) {
		id = libraryChannelRouteParams.params.channelId;
	} else if (streamRouteParams) {
		id = streamRouteParams.params.id;
	} else if (meetingsRouteParams) {
		guid = meetingsRouteParams.params.event;
	}

	const hasEvent = !!guid;
	const skipNode = !id;
	const data = useLazyLoadQuery<AskFellowHooksContextFromURLQuery>(
		graphql`
			query AskFellowHooksContextFromURLQuery($id: ID!, $guid: String!, $hasEvent: Boolean!, $skipNode: Boolean!) {
				node(id: $id) @skip(if: $skipNode) {
					__typename
					... on NoteStream {
						id
						displayName
						otherUser {
							fullName
						}
					}
					... on Channel {
						id
						name
					}
				}

				event: eventByGuid(guid: $guid) @include(if: $hasEvent) {
					... on Event {
						stream {
							id
							displayName
							otherUser {
								fullName
							}
						}
					}
				}
			}
		`,
		{ id: id || "", guid: guid || "", hasEvent, skipNode },
		{ fetchPolicy: "store-and-network" },
	);

	if (libraryAllMeetingsRouteParams && libraryAllMeetingsRouteParams.isExact) {
		return {
			id: null,
			name: "All meetings",
			type: "Channel",
			// Context filters on pseudo-channel can only use baseContextFilters
			// since those are the fields supported by Elasticsearch
			filters: baseContextFilters,
		};
	} else if (libraryMyMeetingsRouteParams && libraryMyMeetingsRouteParams.isExact) {
		return {
			id: null,
			name: "My meetings",
			type: "Channel",
			filters: {
				...baseContextFilters,
				// eslint-disable-next-line camelcase
				user_has_calendar_event: true,
			},
		};
	}

	if (data.event?.stream?.id) {
		// stream as context through calendar event
		const stream = data.event.stream;
		const name = stream.otherUser?.fullName ? stream.otherUser.fullName : stream.displayName;

		return { id: stream.id, name, type: "NoteStream" };
	}

	if (data.node?.__typename === "NoteStream") {
		const stream = data.node;
		const name = stream.otherUser?.fullName ? stream.otherUser.fullName : stream.displayName;

		return { id: stream.id, name, type: data.node?.__typename };
	}

	if (data.node?.__typename === "Channel") {
		const channel = data.node;

		return {
			id: channel.id,
			name: channel.name,
			type: data.node?.__typename,
			filters: contextFilters,
		};
	}

	return undefined;
}

export function useDeleteConversationMutation() {
	const [commit, isInFlight] = useMutationPromise<AskFellowHooksDeleteConversationMutation>(graphql`
		mutation AskFellowHooksDeleteConversationMutation($conversationId: ID!) {
			deleteAskCopilotConversation(conversationId: $conversationId) {
				success
			}
		}
	`);

	const deleteConversation = React.useCallback(
		async (conversationId: string) => {
			await commit({
				variables: { conversationId },
				updater: store => {
					const root = store.getRoot();
					const conversations = root.getLinkedRecords("askCopilotConversations");
					if (!conversations) return;
					const updated = conversations.filter(conversation => conversation.getDataID() !== conversationId);
					root.setLinkedRecords(updated, "askCopilotConversations");
				},
			});
		},
		[commit],
	);

	return [deleteConversation, isInFlight] as const;
}

export function useRenameConversationMutation() {
	const [commit, isInFlight] = useMutationPromise<AskFellowHooksRenameConversationMutation>(graphql`
		mutation AskFellowHooksRenameConversationMutation($conversationId: ID!, $newTitle: String!) @raw_response_type {
			renameAskCopilotConversation(conversationId: $conversationId, newTitle: $newTitle) {
				conversation {
					id
					title
				}
			}
		}
	`);

	const renameConversation = React.useCallback(
		async (conversationId: string, newTitle: string) => {
			await commit({
				variables: { conversationId, newTitle },
				optimisticResponse: {
					renameAskCopilotConversation: {
						conversation: {
							id: conversationId,
							title: newTitle,
						},
					},
				},
			});
		},
		[commit],
	);

	return [renameConversation, isInFlight] as const;
}

export const AskFellowSendMessageMutation = graphql`
	mutation AskFellowHooksSendMessageViaConversationMutation(
		$conversationId: ID!
		$content: String!
		$messageId: String
		$contentForDisplay: String
	) {
		askCopilotSendMessage(
			content: $content
			conversationId: $conversationId
			messageId: $messageId
			contentForDisplay: $contentForDisplay
		) {
			conversation {
				...AskFellowPanelInnerFragment
			}
		}
	}
`;

export function useAskCopilotSendMessageViaConversation(
	conversationKey: AskFellowHooks_useAskCopilotSendMessage_conversation$key,
	firstMessage: boolean,
) {
	const conversation = useFragment(
		graphql`
			fragment AskFellowHooks_useAskCopilotSendMessage_conversation on AskCopilotConversation {
				id
				context {
					name
				}
			}
		`,
		conversationKey,
	);

	const [commit, isInFlight] =
		useMutationPromise<AskFellowHooksSendMessageViaConversationMutation>(AskFellowSendMessageMutation);

	const dispatchAnalyticsTrack = useAnalyticsTrack();

	const sendMessage = React.useCallback(
		(content: string, messageId?: string, contentForDisplay?: string) => {
			dispatchAnalyticsTrack("[Ask Copilot] Sent message", {
				// eslint-disable-next-line camelcase
				conversation_id: conversation?.id,
				firstMessage: !!firstMessage,
				context: conversation?.context?.name,
			});

			return commit({
				variables: {
					conversationId: conversation.id,
					messageId,
					content,
					contentForDisplay,
				},
				onCompleted: (_result, errors) => {
					if (errors) {
						const message = getFirstUserFacingError(errors) ?? "Something went wrong; please try again later.";
						toast.error(message);
					}
				},
				optimisticUpdater(store) {
					const conversationNode = store.get(conversation.id);
					if (!conversationNode) return;

					const id = btoa(`AskCopilotMessage:${v4()}`);
					const newUserMessage = store.create(id, "AskCopilotMessage");
					newUserMessage.setValue(id, "id");
					newUserMessage.setValue("USER", "role");
					newUserMessage.setValue(contentForDisplay ?? content, "content");
					newUserMessage.setValue(false, "pending");
					newUserMessage.setValue(new Date().toISOString(), "createdAt");
					newUserMessage.setValue(false, "isResponse");

					const messages = [...(conversationNode.getLinkedRecords("messages") ?? []), newUserMessage];
					conversationNode.setLinkedRecords(messages, "messages");
				},
			});
		},
		[commit, conversation, dispatchAnalyticsTrack, firstMessage],
	);

	return [sendMessage, isInFlight] as const;
}

/**
 * Check if a sentence is a question
 * examples:
 * - "What is the weather in Tokyo?" => true
 * - "This is a question" => false
 * @param sentence - The sentence to check
 * @returns True if the sentence is a question, false otherwise
 */
export function checkSentenceIsQuestion(sentence: string): boolean {
	const cleaned = sentence.trim().toLowerCase();

	// Direct question if ends with a "?"
	if (cleaned.endsWith("?")) return true;

	// Regex to match if the sentence starts with a question word or modal verb
	const questionRegex =
		/^(who|what|when|where|why|how|which|whose|is|are|am|was|were|do|does|did|can|could|would|will|should|may|might|shall)\b/; // eslint-disable-line max-len

	return questionRegex.test(cleaned);
}

export const askFellowSearchQueryEmitter = new EventEmitter();

export function useAskFellowOpenPanelFromSearch() {
	const dispatchPanel = useNavDispatchPanel();

	const openPanelWithPreFilledQuery = React.useCallback(
		(query: string | null) => {
			localStorage.setItem("ask-fellow-search-query", query ?? "");
			askFellowSearchQueryEmitter.emit("ask-fellow-search-query", query ?? "");
			dispatchPanel("ask-fellow", true, {
				event: "[Ask Copilot] Modal opened from Search entry point",
				properties: {},
				options: {},
			});
		},
		[dispatchPanel],
	);

	return openPanelWithPreFilledQuery;
}

export function useAskFellowButton({
	noteKey,
	source = "suggested_action",
}: {
	noteKey: AskFellowHooks_useAskFellowButton_note$key | null;
	source?: string;
}) {
	const note = useFragment(
		graphql`
			fragment AskFellowHooks_useAskFellowButton_note on Note {
				id
			}
		`,
		noteKey,
	);

	// One of two navbars will be rendered in the app depending on the context.
	// We should open correct the Ask Fellow nav panel accordingly.
	const dispatchNavPanel = useNavDispatchPanel();
	const dispatchCompanionModeNavPanel = useCompanionModeNavDispatchPanel();

	const dispatchPanel = inNewCompanionMode ? dispatchCompanionModeNavPanel : dispatchNavPanel;

	const [createConversation, mutationInProgress] = useCreateConversationMutation();
	const onCreateConversation = React.useCallback(async () => {
		if (mutationInProgress || !note) return;

		try {
			await createConversation(note.id);
			dispatchPanel("ask-fellow", true, {
				event: "[Ask Copilot] Modal opened",
				properties: { source },
				options: {},
			});
		} catch (e) {
			const message = getFirstUserFacingError(e) ?? "Something went wrong; please try again later.";
			toast.error(message);
		}
	}, [note, createConversation, mutationInProgress, dispatchPanel, source]);

	return onCreateConversation;
}
